import { ColorEnum, type GPHalfResponse, type GPEventResponse } from "../../../portal/src/libs/apiClient";
import type { Interval, MergedGpEventResponse, VideoPlayerRefContextValue } from "./types";
import { eventListConfig } from "./types";
import { formatDateForLocaleDisplay } from "../utils/dateTimeFormatUtils";

export function formatDate(date: string | null | undefined, locale: string) {
	return formatDateForLocaleDisplay(locale, date);
}

export function getColorSelectValue(value?: string | null) {
	if (value) {
		return { value: value, label: value };
	}
	return undefined;
}

export function colorSelectValues(translateFn: (value: string) => string) {
	return Object.values(ColorEnum.Values).map((color) => ({
		value: color,
		label: translateFn(`enum.colors.${color.toLowerCase()}`),
	}));
}

export function calculateFootballTimeStamp(event: GPEventResponse, halves: GPHalfResponse[]): string | number {
	const firstHalf = halves.find((item) => item.id === 1);
	const secondHalf = halves.find((item) => item.id === 2);
	const extraHalfOne = halves.find((item) => item.id === 3);
	const extraHalfTwo = halves.find((item) => item.id === 4);
	const extraHalfThree = halves.find((item) => item.id === 5);

	if (event.footballTime) {
		if (event.half === 1) {
			if (secondHalf && event.footballTime > secondHalf.matchTimeStart) {
				const timeDifference = event.footballTime - secondHalf.matchTimeStart;
				if (Math.floor(timeDifference / 60000) > 0) {
					return `${Math.floor(secondHalf.matchTimeStart / 60000)} + ${Math.floor(timeDifference / 60000)}`;
				}
				return Math.floor(event.footballTime / 60000);
			}
			return Math.floor(event.footballTime / 60000);
		}

		if (event.half === 2 && secondHalf && firstHalf) {
			if (event.footballTime > secondHalf.matchTimeStart * 2) {
				const timeDifference = event.footballTime - secondHalf.matchTimeStart * 2;
				if (Math.floor(timeDifference / 60000) > 0) {
					return `${Math.floor(secondHalf.matchTimeStart / 60000) * 2} + ${Math.floor(timeDifference / 60000)}`;
				}
				return Math.floor(secondHalf.matchTimeStart / 60000) * 2;
			}
			return Math.floor(event.footballTime / 60000);
		}

		if (event.half === 3) {
			if (extraHalfTwo && event.footballTime > extraHalfTwo.matchTimeStart) {
				const timeDifference = event.footballTime - extraHalfTwo.matchTimeStart;
				if (Math.floor(timeDifference / 60000) > 0) {
					return `${Math.floor(extraHalfTwo.matchTimeStart / 60000)} + ${Math.floor(timeDifference / 60000)}`;
				}
				return Math.floor(event.footballTime / 60000);
			}
			return Math.floor(event.footballTime / 60000);
		}

		if (event.half === 4 && extraHalfTwo && extraHalfOne) {
			const extraSecondHalfDuration = extraHalfTwo.matchTimeStart - extraHalfOne.matchTimeStart; // 15 mins
			const extraSecondHalfTime = extraHalfTwo.matchTimeStart + extraSecondHalfDuration;
			if (event.footballTime > extraSecondHalfTime) {
				const timeDifference = event.footballTime - extraSecondHalfTime;
				if (Math.floor(timeDifference / 60000) > 0) {
					return `${Math.floor(extraSecondHalfTime / 60000)} + ${Math.floor(timeDifference / 60000)}`;
				}
				return Math.floor(extraSecondHalfTime / 60000);
			}
			return Math.floor(event.footballTime / 60000);
		}

		if (event.half === 5) {
			return Math.floor(event.footballTime / 60000);
		}
	}

	return "";
}

export function getEventTime(event: GPEventResponse) {
	const newEventTime = event?.videoTime / 1000 > 5 ? event?.videoTime / 1000 - 5 : event?.videoTime / 1000;
	return newEventTime * 1000;
}

export function setVideoTime(playerRef: VideoPlayerRefContextValue, videoTime?: number) {
	if (playerRef?.current) {
		if (videoTime !== undefined) {
			playerRef.current.currentTime = videoTime / 1000;
		} else {
			playerRef.current.currentTime = 0;
		}
	}
}

export function handlePlayEvent(playerRef: VideoPlayerRefContextValue, videoTime?: number) {
	if (playerRef?.current) {
		setVideoTime(playerRef, videoTime);

		playerRef.current.play();
	}
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function mergeCloseTimeSpanEvents(events: GPEventResponse[], timeSpanMs = 5000): MergedGpEventResponse[] {
	const gpEvents = events.sort((a, b) => a.videoTime - b.videoTime);

	const merged: Array<MergedGpEventResponse | null> = gpEvents.map((evt) => {
		const videoTime = evt.clipTime !== null ? evt.clipTime : evt.videoTime;
		const prependSeconds = evt.prepend !== null ? evt.prepend / 1000 : eventListConfig.prepend.default;
		const appendSeconds = evt.append !== null ? evt.append / 1000 : eventListConfig.append.default;

		return {
			id: evt.id,
			categories: [evt.category],
			types: [evt.type],
			success: evt.success,
			underPressure: evt.underPressure,
			gamePlayerId: evt.gamePlayerId,
			gameTeamId: evt.gameTeamId,
			playerName: evt.playerName,
			backNumber: evt.backNumber,
			x: evt.x,
			y: evt.y,
			half: evt.half,
			halfTime: evt.halfTime,
			halfOvertime: evt.halfOvertime,
			footballTime: evt.footballTime,
			videoTime: videoTime,
			lastVideoTime: videoTime,
			startTime: Math.max(videoTime - prependSeconds * 1000, 0),
			endTime: videoTime + appendSeconds * 1000,
			providerEventId: evt.providerEventId,
			relativeProviderEventId: evt.relativeProviderEventId,
			details: evt.details ? { ...evt.details } : null,
			prependSeconds: prependSeconds,
			appendSeconds: appendSeconds,
			hits: [videoTime],
			isMutated: false,
			eventIds: [evt.id],
			comments: evt.comments,
			teamClubLogo: evt.teamClubLogo ?? null,
			teamShirtColor: evt.teamShirtColor ?? null,
		};
	});

	for (let i = 0; i < merged.length; i++) {
		const baseMergedEvent = merged[i];
		if (!baseMergedEvent) {
			continue;
		}

		const baseSourceEvent = gpEvents[i];

		for (let j = i + 1; j < merged.length; j++) {
			const compareMergedEvent = merged[j];
			if (!compareMergedEvent) {
				continue;
			}

			if (gpEvents[j].videoTime >= baseSourceEvent.videoTime + timeSpanMs) {
				continue;
			}

			if (baseMergedEvent.gamePlayerId !== null && compareMergedEvent.gamePlayerId === baseMergedEvent.gamePlayerId) {
				baseMergedEvent.videoTime = Math.min(baseMergedEvent.videoTime, compareMergedEvent.videoTime);
				baseMergedEvent.lastVideoTime = Math.max(baseMergedEvent.lastVideoTime, compareMergedEvent.lastVideoTime);
				baseMergedEvent.eventIds.push(compareMergedEvent.id);

				for (const cat of compareMergedEvent.categories) {
					if (!baseMergedEvent.categories.includes(cat)) {
						baseMergedEvent.categories.push(cat);
					}
				}

				for (const t of compareMergedEvent.types) {
					if (!baseMergedEvent.types.includes(t)) {
						baseMergedEvent.types.push(t);
					}
				}

				if (compareMergedEvent.details) {
					if (baseMergedEvent.details) {
						for (const [key, val] of Object.entries(compareMergedEvent.details)) {
							if (!(key in baseMergedEvent.details)) {
								baseMergedEvent.details[key] = val;
							}
						}
					} else {
						baseMergedEvent.details = { ...compareMergedEvent.details };
					}
				}

				baseMergedEvent.hits.push(compareMergedEvent.videoTime);
				baseMergedEvent.startTime = baseMergedEvent.videoTime - baseMergedEvent.prependSeconds * 1000;
				baseMergedEvent.endTime = baseMergedEvent.lastVideoTime + baseMergedEvent.appendSeconds * 1000;

				merged[j] = null;
			}
		}
	}

	return merged.filter((evt): evt is MergedGpEventResponse => evt !== null);
}

export function createIntervalsFromMergedEvents(mergedEvents: MergedGpEventResponse[]): Interval[] {
	if (mergedEvents.length === 0) {
		return [];
	}

	const sortedIntervals: Interval[] = mergedEvents
		.map((e) => ({ start: e.startTime, end: e.endTime, length: e.endTime - e.startTime }) as Interval)
		.slice()
		.sort((a, b) => a.start - b.start);
	const merged: Map<number, Interval> = new Map<number, Interval>();

	let current = {
		start: sortedIntervals[0].start,
		end: sortedIntervals[0].end,
		length: sortedIntervals[0].end - sortedIntervals[0].start,
	} as Interval;

	for (let i = 1; i < sortedIntervals.length; i++) {
		const next = sortedIntervals[i];

		if (next.start <= current.end) {
			current.end = Math.max(current.end, next.end);
		} else {
			merged.set(current.start, current);
			current = next;
		}
	}

	merged.set(current.start, current);

	return Array.from(merged.values());
}

export function parseTime(time: number): { minutes: number; seconds: number } {
	const seconds = Math.floor((time / 1000) % 60);
	let minutes = Math.floor((time / 1000 / 60) % 60);
	const hours = Math.floor((time / 1000 / 60 / 60) % 24);

	minutes = hours > 0 ? hours * 60 + minutes : minutes;

	return { minutes, seconds };
}

export function calculateEventTime(event: MergedGpEventResponse | GPEventResponse): string {
	if (event.footballTime !== null && event.halfOvertime !== null && event.halfOvertime !== undefined) {
		if (event.halfOvertime === 0) {
			const parsedTime = parseTime(event.footballTime);

			return [parsedTime.minutes.toString().padStart(2, "0"), parsedTime.seconds.toString().padStart(2, "0")].join(":");
		}

		const formalTimeBase = event.footballTime - event.halfOvertime;
		const parsedTime = parseTime(event.halfOvertime);

		return `${Math.floor(formalTimeBase / 60000)} + ${[
			parsedTime.minutes.toString().padStart(2, "0"),
			parsedTime.seconds.toString().padStart(2, "0"),
		].join(":")}`;
	}

	return "";
}

export function getParentWindowsOrigin(): string {
	const windowUrl = window.location.origin;
	const locAncestor = document.location.ancestorOrigins;
	let locAncestorUri = null;

	if (locAncestor) {
		locAncestorUri = locAncestor.item(locAncestor.length - 1);
	}
	if (locAncestorUri != null && locAncestorUri !== windowUrl) {
		return locAncestorUri;
	}
	if (document.referrer !== "" && document.referrer !== windowUrl) {
		return new URL(document.referrer).origin;
	}
	if (!document.location.href.includes(windowUrl)) {
		return new URL(document.location.href).origin;
	}
	return windowUrl;
}

export function getTextColorForBackground(color: string) {
	function parseCssColor(inputColor: string) {
		const element = document.createElement("div");
		element.style.color = inputColor;
		document.body.appendChild(element);

		const computedColor = window.getComputedStyle(element).color;
		document.body.removeChild(element);

		const [r, g, b] = computedColor
			.replace(/^rgba?\(|\s+|\)$/g, "")
			.split(",")
			.map(Number);

		return { r, g, b };
	}

	function getRelativeLuminance({ r, g, b }: { r: number; g: number; b: number }) {
		const [R, G, B] = [r, g, b].map((val) => {
			const channel = val / 255;
			return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
		});
		return 0.2126 * R + 0.7152 * G + 0.0722 * B;
	}

	function contrastRatio(lum1: number, lum2: number) {
		const lighter = Math.max(lum1, lum2);
		const darker = Math.min(lum1, lum2);
		return (lighter + 0.05) / (darker + 0.05);
	}

	const rgbColor = parseCssColor(color);

	// const rgbBlack = { r: 0, g: 0, b: 0 };
	const rgbWhite = { r: 255, g: 255, b: 255 };

	const lumColor = getRelativeLuminance(rgbColor);
	const lumWhite = getRelativeLuminance(rgbWhite);
	// const lumBlack = getRelativeLuminance(rgbBlack);

	const ratioWithWhite = contrastRatio(lumColor, lumWhite);
	// const ratioWithBlack = contrastRatio(lumColor, lumBlack);

	if (ratioWithWhite < 3) {
		return "black";
	}
	return "white";
}

export function getEventColor(event: GPEventResponse) {
	const red = "#d3084c";
	const green = "#32ff7e";
	const yellow = "#f6f355";

	if (event?.type == "take_on" && event?.success == false
		|| event?.type == "aerial_duel" && event?.success == false
		|| event?.type == "pass" && event?.success == false
		|| event?.type == "pass_received" && event?.success == false
	) {
		return red;
	} else if (event?.type == "shot") {
		if (event?.details?.on_target == "off") {
			return red;
		} else if (event?.details?.on_target == "on") {
			return green;
		} else if (event?.details?.on_target == "blocked") {
			return yellow;
		}
	} else if (event?.type == "tackle") {
		if (event?.success == true) {
			return green;
		} else if (event?.success == false) {
			return red;
		}
	} else if (event?.type == "catch_interception") {
		if (event?.success == true) {
			return green;
		} else if (event?.success == false) {
			return red;
		}
	} else if (event?.type == "card") {
		if (event?.details?.card_type == "yellow") {
			return yellow;
		} else if (event?.details?.card_type == "red") {
			return red;
		}
	}

	return green;
}

/**
 * Use default window.crypto.randomUUID() if available,
 * otherwise use a fallback implementation
 * 
 * @returns a random UUID
 */
export function randomUUID(): string {
	if (!("randomUUID" in crypto)) {
		return (
			(1e7 + -1e3 + -4e3 + -8e3 + -1e11).toString()
		).replace(/[018]/g, (c: string) =>
			(
				Number(c) ^
				(crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
			).toString(16)
		);
	}

	return crypto.randomUUID();
}

export function excludeDirectionalEvents(events: GPEventResponse[]): GPEventResponse[] {
	return events.filter((event) => {
		// "No direction" may be represented by the API as:
		// - missing key (undefined)
		// - null
		// - empty string
		const direction = (event.details as Record<string, unknown> | null | undefined)?.direction;

		if (direction === undefined || direction === null) {
			return true;
		}

		if (typeof direction === "string" && direction.trim() === "") {
			return true;
		}

		return false;
	});
}
